import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Result, Segmented, Select, Space, Spin, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { adminApi } from '@services/api';
import { useLatestRequest } from '@hooks/useLatestRequest';
import { apiErrorMessage } from '@utils/apiError';
import type { BqFilter, BqInsightsResponse, BqPlatform } from '@/types';
import { BqInsightsBoard } from './BqInsightsBoard';
import { dayLabel, isFiltered } from './bqMath';

const { Title, Text } = Typography;

const RANGES = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

const ALL_VERSIONS = '__all__';

const PLATFORMS: Array<{ value: BqPlatform; label: string }> = [
  { value: 'all', label: 'All platforms' },
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
];

const PLATFORM_LABEL: Record<BqPlatform, string> = {
  all: 'all platforms',
  android: 'Android',
  ios: 'iOS',
};

/** What the filter applies to, for the note under the header. */
const filterNote = (data: BqInsightsResponse): string => {
  const parts = [
    data.platform !== 'all' ? PLATFORM_LABEL[data.platform] : null,
    data.version != null
      ? data.version === 'unknown'
        ? 'unknown app version'
        : `app version ${data.version}`
      : null,
  ].filter(Boolean);
  return (
    `Filtered to ${parts.join(', ')}: KPI tiles, trends, retention (by install-day platform and version) and the ` +
    'FTUE funnel (day-0 funnel, as on FTUE & Progression). Things to look at, models and segments are all ' +
    'platforms and versions.'
  );
};

/** "All versions" plus the range's versions (most played first); keeps the current pick listed. */
const versionOptions = (data: BqInsightsResponse, current: string | null) => {
  const list = (data.versions ?? []).map((v) => ({
    value: v.version,
    label: `${v.version === 'unknown' ? 'Unknown version' : `v${v.version}`} · ${v.playerDays.toLocaleString('en-IN')} player-days`,
  }));
  if (current !== null && !list.some((o) => o.value === current)) {
    list.unshift({ value: current, label: `v${current}` });
  }
  return [{ value: ALL_VERSIONS, label: 'All versions' }, ...list];
};

const istTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

/**
 * KPIs, retention, FTUE, segments, rule-based findings and model scores from the
 * BigQuery analytics layer (GA4 export). The layer is rebuilt every 4 hours and the
 * backend caches each answer for 30 minutes, so this page does not poll.
 */
export const BqInsightsPage: React.FC = () => {
  const [days, setDays] = useState(30);
  /** version null = all versions. */
  const [filter, setFilter] = useState<BqFilter>({ platform: 'all', version: null });
  const { version } = filter;
  const [data, setData] = useState<BqInsightsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState<string | null>(null);
  const beginRequest = useLatestRequest();

  const fetchInsights = useCallback(
    async (range: number, f: BqFilter) => {
      const isLatest = beginRequest();
      setLoading(true);
      try {
        const res = await adminApi.getBqInsights(range, f);
        if (!isLatest()) return;
        setData(res.data);
        setFailed(null);
      } catch (err) {
        if (!isLatest()) return;
        console.error('Failed to load BigQuery insights', err);
        setFailed(apiErrorMessage(err, 'Failed to load BigQuery insights.'));
      } finally {
        if (isLatest()) setLoading(false);
      }
    },
    [beginRequest]
  );

  useEffect(() => {
    void fetchInsights(days, filter);
  }, [fetchInsights, days, filter]);

  if (!data) {
    if (failed) {
      return (
        <Result
          status="error"
          title="Couldn't load BigQuery insights"
          subTitle={failed}
          extra={
            <Button
              type="primary"
              onClick={() => void fetchInsights(days, filter)}
              loading={loading}
            >
              Retry
            </Button>
          }
        />
      );
    }
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>
            BigQuery Insights
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {data.dataThrough
              ? `Data through ${dayLabel(data.dataThrough)} (IST days)`
              : 'No data in this range'}
            {' · rebuilt every 4 h · fetched '}
            {istTime(data.generatedAt)} IST
          </Text>
        </div>
        <Space size={12} wrap>
          {failed && (
            <Text type="warning" style={{ fontSize: 13 }}>
              {failed}
            </Text>
          )}
          <Select
            size="small"
            aria-label="Platform"
            style={{ minWidth: 130 }}
            value={filter.platform}
            options={PLATFORMS}
            onChange={(platform: BqPlatform) => setFilter((f) => ({ ...f, platform }))}
          />
          <Select
            size="small"
            aria-label="App version"
            style={{ minWidth: 170 }}
            value={version ?? ALL_VERSIONS}
            onChange={(v: string) =>
              setFilter((f) => ({ ...f, version: v === ALL_VERSIONS ? null : v }))
            }
            options={versionOptions(data, version)}
            disabled={(data.versions ?? []).length === 0 && version === null}
            title={
              (data.versions ?? []).length === 0 && version === null
                ? 'No per-version data in this range yet'
                : 'Filter KPIs, retention and FTUE to one app version'
            }
          />
          <Segmented
            size="small"
            options={RANGES}
            value={days}
            onChange={(v) => setDays(Number(v))}
          />
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => void fetchInsights(days, filter)}
          >
            Refresh
          </Button>
        </Space>
      </div>
      {isFiltered(data) && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={filterNote(data)}
          description={
            data.platform === 'ios'
              ? 'Only a few iOS devices are in the GA4 export so far (12 players on 2 Nov), so iOS numbers are tiny.'
              : undefined
          }
        />
      )}
      <BqInsightsBoard data={data} />
    </div>
  );
};
